import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getFilteredTableData } from "../../../../../../shared/utils/tableUtils"
import { FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip, Typography, Box } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_SortingState, MRT_TableInstance } from "material-react-table"
import {
    faUser,
    faCircleEuro,
    faTrash,
    faPrint,
    faList,
    faReceipt,
    faFileArrowDown,
    faArrowProgress,
    faLock,
    faLockOpen,
} from "@fortawesome/pro-light-svg-icons"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"

import { useExportMeetingServiceOrdersToCsv } from "../components/useExportMeetingServiceOrdersToCsv"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict, LocalizedEnum } from "../../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import {
    ActionBarItem,
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../../shared/components/table/FloatingActionBar"
import {
    MeetingServiceBookDialogContent,
    MeetingServiceDeleteDialogContent,
} from "../components/MeetingServiceDialogContent"
import {
    MeetingServiceOrdersResponse,
    changeBookStatus,
    fetchMeetingServiceOrders,
    MerchantMeetingServiceOrdersQuery,
    undoBatchStatusUpdate,
    deleteMeetingServiceOrders,
    changeLockStatusMerchant,
} from "../meetingServiceApi"
import {
    DeliveryType,
    DeliveryTypeFilter,
    FilteredStatuses,
    MeetingServiceOrder,
    MeetingServiceOrderStatus,
    MeetingServiceOrdersView,
    TimePeriodMeetingService,
    getDeliveryTypeValue,
    getTimePeriod,
} from "../meetingServiceTypes"
import { FiltersDrawerMeetingService } from "../../../../../supplier/reporting/accounting/meetingService/components/FiltersDrawerMeetingService"
import { useMeetingServiceFilters } from "../context/MeetingServiceFiltersContext"
import { useLocationQuery } from "../../../../../../core/router/routerHooks"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { LocalCache, StorageKey } from "../../../../../../shared/cache/LocalCache"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { OrderPackageSlip } from "../components/OrderPackageSlip"
import { MinimizedStatusButton, StatusButton } from "../components/StatusMenu"
import { StatusChangeModal } from "../components/StatusChangeModal"
import { enqueueSnackbar } from "notistack"
import UndoSnackBar, { UNDO_TIMEOUT } from "../components/UndoSnackbar"
import { useQueryClient } from "@tanstack/react-query"
import { STATUS_MAP } from "../constants"
import { UserMeetingsReceipt } from "../../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"

import { ProductionOrderMenu } from "../components/ProductionOrderMenu"
import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"
import { getOrderPerson } from "../../../../../../shared/utils/getOrderPerson"
import { Row } from "@tanstack/react-table"
import {
    StandardSuccessDialog,
    SuccessDialogType,
} from "../../../../../../shared/components/dialogs/StandardSuccessDialog"
import { MerchantHomeQuery } from "../../../home/merchantHomeApi"
import { useCreditNoteDialog } from "../../../../../../shared/components/dialogs/creditNoteDialog/useCreditNoteDialog"
import { DeliveryListMode, OrderDeliveryList } from "../components/OrderDeliveryList"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LoadingIconButton } from "../../../../../../shared/components/buttons/LoadingButton"
import { Logger } from "../../../../../../lib/logging"
import { faArrowRotateRight } from "@fortawesome/sharp-regular-svg-icons"
import { useDateTime } from "../../../../../localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        maxWidth: "11rem",
        ".MuiSelect-select": {
            padding: "0.5rem 1rem",
        },
    },
    deliveryTypeFilter: {
        minWidth: "12.5rem",
    },
    tableHeadRow: {
        "& > th:first-of-type": {
            borderLeft: "4px solid transparent",
        },
    },
    tableBodyRow: {
        "& > td:first-of-type": {
            borderLeft: "4px solid transparent",
        },
    },
    tableBodyRowAccent: {
        "& > td:first-of-type": {
            borderLeftColor: theme.palette.tailwind.amber[400],
        },
    },
    wrapIconText: {
        display: "flex",
        gap: "2px",
        alignItems: "center",
    },
    inlineIconWrapper: {
        display: "inline-block",
        marginLeft: "0.5rem",
        whiteSpace: "nowrap",
    },
    inlineIcon: {
        verticalAlign: "middle",
        color: "#334155",
    },
    inlineIconText: {
        marginLeft: "0.25rem",
    },
    classNameOverride: {
        paddingBottom: "4rem",
    },
    changeTimeButtonSelect: {
        cursor: "pointer",
        fontSize: "0.875rem",
        marginRight: "2rem",
    },
    table: {
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
        // Align MuiCollapse (search field) with toolbar internal actions
        "& .MuiCollapse-wrapperInner": {
            display: "flex",
            alignItems: "center",
        },
    },
    filterButtons: {
        paddingTop: 0,
    },
    lockedIcon: {
        color: theme.palette.tailwind.slate[700],
    },
    unlockedIcon: {
        color: theme.palette.tailwind.green[500],
    },
    exportSelectedContainer: {
        width: "7rem",
    },
    refreshButton: {
        padding: "0 0.75rem",
    },
    rightActionButton: {
        whiteSpace: "nowrap",
    },
}))

const selectMeetingService = (data: MeetingServiceOrdersResponse) => data?.orders || []

export function getHiddenOrderStatuses(view: MeetingServiceOrdersView): MeetingServiceOrderStatus[] {
    switch (view) {
        case MeetingServiceOrdersView.NOT_APPROVED:
            return [
                MeetingServiceOrderStatus.NOT_APPROVED,
                MeetingServiceOrderStatus.REJECTED,
                MeetingServiceOrderStatus.PREPARING,
                MeetingServiceOrderStatus.DELIVERED,
                MeetingServiceOrderStatus.TO_BE_BOOKED,
                MeetingServiceOrderStatus.BOOKED,
                MeetingServiceOrderStatus.READY,
            ]
        case MeetingServiceOrdersView.TO_BE_BOOKED:
            return [
                MeetingServiceOrderStatus.NOT_APPROVED,
                MeetingServiceOrderStatus.APPROVED,
                MeetingServiceOrderStatus.REJECTED,
                MeetingServiceOrderStatus.DELIVERED,
                MeetingServiceOrderStatus.PREPARING,
                MeetingServiceOrderStatus.READY,
            ]
        case MeetingServiceOrdersView.PRODUCTION:
            return [
                MeetingServiceOrderStatus.APPROVED,
                MeetingServiceOrderStatus.REJECTED,
                MeetingServiceOrderStatus.NOT_APPROVED,
                MeetingServiceOrderStatus.TO_BE_BOOKED,
                MeetingServiceOrderStatus.PREPARING,
                MeetingServiceOrderStatus.READY,
            ]

        default:
            return []
    }
}
type DeliveryListState = Readonly<{
    open: boolean
    mode: DeliveryListMode
}>

export enum DialogType {
    BOOK = "book",
    DELETE = "delete",
}
export enum CreatePDFType {
    SLIP = "slip",
    RECEIPT = "receipt",
}
const defaultDialogState = {
    showConfirmDialog: false,
    items: [],
    type: DialogType.BOOK,
}

export type ConfirmationState = {
    showConfirmDialog: boolean
    items: MeetingServiceOrder[]
    type: DialogType
}

export enum MEETING_SERVICE_QUERY_PARAM {
    PERIOD = "period",
    READY_FOR_INVOICING = "readyForInvoicing",
    SHOW_UNAPPROVED_ORDERS = "showUnapprovedOrders",
}

export const statusOrderList = [
    {
        id: 0,
        name: MEETING_SERVICE_QUERY_PARAM.SHOW_UNAPPROVED_ORDERS,
    },
    {
        id: 1,
        name: "isBooked",
    },
]
export type FilterOrdersProps = Readonly<{
    ordersStatus: string[]
    customersId: { customerId: string | undefined; groupId: string | undefined }[]
}>

export type ActionType = "update_meeting_service" | "duplicate_meeting_service"

type MeetingServiceTabProps = {
    tab?: MeetingServiceOrderStatus
    view: MeetingServiceOrdersView
}

const disabledFilters = {
    disableDeliveryDate: true,
    disableInvoicing: true,
    disablePendingApproval: true,
    disableMerchantShops: true,
    disableServicePayment: true,
}

export default function MeetingServiceTab({ tab, view }: MeetingServiceTabProps) {
    const { filtersList, setFiltersList, activeCount } = useMeetingServiceFilters()
    const { classes, cx } = useStyles()
    const { merchantId } = useMerchant()
    const query = useLocationQuery()
    const dateTimeFactory = useDateTime()

    const todayDate = dateTimeFactory.now()
    const queryShowUnapprovedOrders = query.get(MEETING_SERVICE_QUERY_PARAM.SHOW_UNAPPROVED_ORDERS)

    const { l10n } = useAppLocalization()
    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const [timePeriod, setTimePeriod] = useState<TimePeriodMeetingService>(() => {
        const saved = LocalCache().get(StorageKey.MEETING_SERVICE_TIME_PERIOD)
        if (saved && Object.values(TimePeriodMeetingService).includes(saved as TimePeriodMeetingService)) {
            return saved as TimePeriodMeetingService
        }
        return TimePeriodMeetingService.THIS_WEEK
    })

    const [deliveryType, setDeliveryType] = useState<DeliveryTypeFilter>(DeliveryTypeFilter.ALL)
    const [cancelResourceBooking, setCancelResourceBooking] = useState<boolean>(false)
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
    const [cancellationMessage, setCancellationMessage] = useState<string>("")

    const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [globalFilter, setGlobalFilter] = useState<string>("")
    const [showGlobalFilter, setShowGlobalFilter] = useState<boolean>(false)
    const [showSuccessModal, setShowSuccessModal] = useState<{
        open: boolean
        type?: SuccessDialogType
        ordersCount?: number
        closeCallback?: () => void
    } | null>(null)

    const queryClient = useQueryClient()

    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const [activeRow, setActiveRow] = useState<MeetingServiceOrder>()
    const orderIds = selectedRows ? selectedRows.map((order) => order.id) : []
    const [receiptModal, setReceiptModal] = useState<{
        open: boolean
        activeOrderId?: number
        orders: MeetingServiceOrder[]
        singleMode?: boolean
    }>({
        open: false,
        orders: [],
        singleMode: true,
    })
    const [showPackSlipModal, setShowPackSlipModal] = useState(false)
    const [selectedStatus, setSelectedStatus] = useState<FilteredStatuses | undefined>()
    const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
    const [deliveryListState, setDeliveryListState] = useState<DeliveryListState>({
        open: false,
        mode: "selected",
    })

    const [singleTableItemId, setSingleTableItemId] = useState<number | undefined>(undefined)

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)
    const [confirmDialogState, setConfirmDialogState] = useState<ConfirmationState>({ ...defaultDialogState })

    const excludeDateTime = [MeetingServiceOrderStatus.NOT_APPROVED, MeetingServiceOrderStatus.TO_BE_BOOKED].some(
        (t) => t === tab,
    )

    useEffect(() => {
        const queryPeriod = query.get(MEETING_SERVICE_QUERY_PARAM.PERIOD)
        if (queryPeriod) {
            const periodValue = Object.values(TimePeriodMeetingService).find(
                (value) => value.toLowerCase() === queryPeriod.toLowerCase(),
            )
            if (periodValue) {
                setTimePeriod(periodValue)
                LocalCache().set(StorageKey.MEETING_SERVICE_TIME_PERIOD, timePeriod)
            }
        }
    }, [query.get(MEETING_SERVICE_QUERY_PARAM.PERIOD)])

    useEffect(() => {
        !!queryShowUnapprovedOrders &&
            setFiltersList((prev) => {
                return {
                    ...prev,
                    ordersStatus: ["0"],
                }
            })
    }, [])

    const meetingServiceDependencies = useMemo(() => {
        const {
            // Exclude these from rest - they are handled explicitly or not needed
            startBookedDate: _startBookedDate,
            endBookedDate: _endBookedDate,
            startDeliveryDate: _startDeliveryDate,
            endDeliveryDate: _endDeliveryDate,
            pendingApproval: _pendingApproval,
            invoiceStatus: _invoiceStatus,
            isBooked: _isBooked,
            ...restFilters
        } = filtersList

        return {
            ...restFilters,
            merchantId,
            view,
            deliveryType,
            period:
                isDatePicker || timePeriod === TimePeriodMeetingService.ALL ? TimePeriodMeetingService.ALL : timePeriod,
            startDeliveryDate:
                isDatePicker && !excludeDateTime
                    ? (filtersList.startDeliveryDate?.toISODate() ?? undefined)
                    : undefined,
            endDeliveryDate:
                isDatePicker && !excludeDateTime ? (filtersList.endDeliveryDate?.toISODate() ?? undefined) : undefined,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            satelliteIds: filtersList.satelliteIds?.join(","),
            buyerPartyIds: filtersList.buyerPartyIds?.join(","),
            paymentMethod: filtersList.paymentMethod?.join(","),
        }
    }, [filtersList, merchantId, deliveryType, isDatePicker, timePeriod, excludeDateTime, view])

    const meetingServiceResult = useApiQuery(fetchMeetingServiceOrders, {
        queryName: MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies: meetingServiceDependencies,
        select: selectMeetingService,
    })

    const allowedOrderIds = useMemo(() => {
        if (!selectedStatus || (!selectedRows && !activeRow)) return []
        const rows = activeRow ? [activeRow] : selectedRows || []
        const allowedIds = rows
            ?.filter(
                (order) =>
                    order.orderAction.statusTransitions?.allowed?.[selectedStatus] ||
                    !order.orderAction.statusTransitions?.disallowed?.[selectedStatus],
            )
            .map((order) => order.id)

        const logger = new Logger("meeting-service")
        logger.info(
            `Calculated allowed order ids [selected rows: ${rows.length}, allowed order ids: ${allowedIds.length}]`,
        )

        return allowedIds
    }, [selectedStatus, selectedRows, activeRow])

    const { creditNoteDialog, openCreditNoteDialog } = useCreditNoteDialog()

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceResult)

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const deleteCallback = () => {
        queryClient.invalidateQueries({
            queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
        })
        queryClient.invalidateQueries({
            queryKey: [MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS],
        })
    }

    const { mutate: callChangeBookStatus } = useApiMutation(changeBookStatus, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    const { mutate: callDeleteOrders, isLoading: isDeletingOrders } = useApiMutation(deleteMeetingServiceOrders, {
        async onSuccess(params, _data) {
            handleClearSelected()
            setCancellationMessage("")
            setShowSuccessModal({
                open: true,
                type: "deleteOrder",
                ordersCount: params.orderIds?.length ?? 0,
                closeCallback: deleteCallback,
            })
        },
    })
    const { mutate: undoBatchUpdate } = useApiMutation(undoBatchStatusUpdate, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    const { mutate: callChangeLockStatus } = useApiMutation(changeLockStatusMerchant, {
        async onMutate(params) {
            const previousData = queryClient.getQueryData<MeetingServiceOrdersResponse>([
                MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
                meetingServiceDependencies,
            ])?.orders

            queryClient.setQueryData<MeetingServiceOrdersResponse>(
                [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE, meetingServiceDependencies],
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        orders: old.orders.map((order) =>
                            params.updateLockStatus.orderIds.includes(order.id)
                                ? {
                                      ...order,
                                      deliveryDetails: {
                                          ...order.deliveryDetails,
                                          isLocked: params.updateLockStatus.isLocked,
                                      },
                                  }
                                : order,
                        ),
                    }
                },
            )

            return { previousData }
        },
        async onError(_err, _params, context) {
            if ("previousData" in context && context.previousData) {
                context.queryClient.setQueryData<MeetingServiceOrdersResponse>(
                    [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE, meetingServiceDependencies],
                    (old) => {
                        if (!old) return old
                        return {
                            ...old,
                            orders: context.previousData!,
                        }
                    },
                )
            }
        },
    })

    const handleLockStatusChange = useCallback(
        (orderIds: number[], isLocked: boolean) => {
            if (!orderIds.length) return
            callChangeLockStatus({
                updateLockStatus: {
                    isLocked,
                    orderIds,
                },
            })
        },
        [callChangeLockStatus],
    )

    const onStatusClick =
        (placement: "top" | "bottom") => (e: React.MouseEvent<HTMLElement>, row?: MeetingServiceOrder) => {
            if (menuPos?.left || menuPos?.top) {
                setMenuPos(null)
                setActiveRow(undefined)
            } else {
                const rect = e.currentTarget.getBoundingClientRect()
                setActiveRow(row)
                setMenuPos((prev) =>
                    prev
                        ? null
                        : placement === "bottom"
                          ? { top: rect.bottom, left: rect.right }
                          : { top: rect.top, left: rect.right },
                )
            }
        }

    const globalFilterFn = (row: Row<MeetingServiceOrder>, _columnId: string, filterValue: string) => {
        if (!filterValue) return true
        const search = filterValue.toLowerCase()

        const { responsible } = getOrderPerson(row.original?.organizers)
        const { id, meetingDetails, deliveryDetails } = row.original

        const meetingName = meetingDetails?.meetingName?.toLowerCase() || ""
        const location = deliveryDetails?.location?.name?.toLowerCase() || ""
        const idStr = String(id).toLowerCase()

        const match =
            idStr.includes(search) ||
            meetingName.includes(search) ||
            responsible?.name?.toLowerCase()?.includes(search) ||
            location.includes(search)

        return match
    }

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("meeting-service-table-order-id-column"),
                accessorKey: "id",
                size: 150,
                minSize: 150,
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                    size: 80,
                },
                (row) => row?.deliveryDetails.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-delivery-location-column"),
                accessorKey: "deliveryDetails",
                size: 420,
                Cell: ({ row }) => {
                    const deliveryTypeValue = getDeliveryTypeValue(row.original.deliveryDetails.type)
                    const { responsible } = getOrderPerson(row.original?.organizers)

                    return (
                        <StandardTwoLinesCell
                            title={
                                <>
                                    {row.original.meetingDetails.meetingName}
                                    {!!row.original.deliveryDetails.sittings && (
                                        <span className={cx(classes.wrapIconText, classes.inlineIconWrapper)}>
                                            <FontAwesomeIcon icon={faUser} className={classes.inlineIcon} />
                                            <span className={classes.inlineIconText}>
                                                {row.original.deliveryDetails.sittings}
                                            </span>
                                        </span>
                                    )}
                                    {row.original.paymentDetails?.isContantPayment && (
                                        <FontAwesomeIcon
                                            icon={faCircleEuro}
                                            color={"#334155"}
                                            style={{ marginLeft: "0.5rem" }}
                                        />
                                    )}
                                </>
                            }
                            subtitle1={
                                deliveryTypeValue === DeliveryType.DELIVERY ? (
                                    <LocalizedStrict
                                        id="meeting-service-table-meeting"
                                        vars={{ locationName: row.original.deliveryDetails.location.name }}
                                    >
                                        <span>{`Delivery: ${row.original.deliveryDetails.location.name}`}</span>
                                    </LocalizedStrict>
                                ) : (
                                    <LocalizedEnum
                                        base="delivery-type-filter"
                                        enumValue={row.original.deliveryDetails.type}
                                    >
                                        <span>{deliveryTypeValue}</span>
                                    </LocalizedEnum>
                                )
                            }
                            subtitle2={responsible?.name}
                        />
                    )
                },
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("meeting-service-table-amount-column"),
                    id: "amount",
                    size: 180,
                    minSize: 180,
                },
                (row) => row.amount,
                true,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-lock-column"),
                accessorKey: "deliveryDetails.isLocked",
                size: 120,
                enableSorting: false,
                Cell: ({ row }) => {
                    const isLocked = Boolean(row.original.deliveryDetails.isLocked)
                    const tooltipId = isLocked
                        ? "meeting-service-table-lock-column-lock-tooltip"
                        : "meeting-service-table-lock-column-unlock-tooltip"

                    return (
                        <LocalizedStrict id={tooltipId} attrs={{ title: true }}>
                            <Tooltip arrow title={l10n.getString(tooltipId)}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleLockStatusChange([row.original.id], !isLocked)}
                                >
                                    <FontAwesomeIcon
                                        icon={isLocked ? faLock : faLockOpen}
                                        className={isLocked ? classes.lockedIcon : classes.unlockedIcon}
                                    />
                                </IconButton>
                            </Tooltip>
                        </LocalizedStrict>
                    )
                },
            }),
            columnFactory.statusColumn({
                id: "status",
                header: l10n.getString("service-center-admin-organization-resources-table-header-status"),
                accessorFn: (row) => row.deliveryDetails.statusDetails.status,
                size: 200,
                minSize: 200,
                Cell: ({ row }) => {
                    return (
                        <MinimizedStatusButton
                            onClick={(e) => onStatusClick("bottom")(e, row.original)}
                            status={row.original.deliveryDetails.statusDetails.status}
                            tooltipLabel={row.original.deliveryDetails.statusDetails.message}
                        />
                    )
                },
            }),
        ],
        [l10n, columnFactory, handleLockStatusChange],
    )

    const handleChangeTimeOrDatePicker = () => setIsDatePicker((prev) => !prev)

    const handleChangeDeliveryType = (event: any) => {
        setDeliveryType(event.target.value)
    }

    const exportSelectedOrders = useExportMeetingServiceOrdersToCsv({
        data: selectedRows,
        startDate: isDatePicker
            ? (filtersList.startDeliveryDate ?? todayDate)
            : (getTimePeriod(dateTimeFactory, timePeriod)?.start ?? todayDate),
        endDate: isDatePicker
            ? (filtersList.endDeliveryDate ?? todayDate)
            : (getTimePeriod(dateTimeFactory, timePeriod)?.end ?? todayDate),
        includeVatInLabels: true,
    })

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const handleCloseReceipt = () => {
        setReceiptModal({ open: false, orders: [] })
    }

    const handleClosePackSlip = () => setShowPackSlipModal(false)

    const handleCloseBatchModal = () => {
        setShowStatusChangeModal(false)
        handleClearSelected()
    }

    const onStatusUpdate = (status: FilteredStatuses) => {
        setSelectedStatus(status)
        setShowStatusChangeModal(true)
    }

    const onStatusChangeSnackbarUndo = async () => {
        await undoBatchUpdate({
            updateDeliveryStatus: {
                undo: true,
                orderIds,
            },
        })
        setSelectedStatus(undefined)
    }

    const onStatusChangeSnackbarClose = () => {
        setSelectedStatus(undefined)
        queryClient.invalidateQueries({
            queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
        })
    }

    const setShowUndo = (orderCount: number) => {
        const { stateTranslationId } = STATUS_MAP[selectedStatus!]

        const translation = `${l10n.getString("meeting-service-snackbar-undo-message", {
            amount: orderCount,
        })} ${l10n.getString(stateTranslationId)}`

        enqueueSnackbar(translation, {
            content: (key, message) => (
                <UndoSnackBar
                    undoLabel={l10n.getString("meeting-service-snackbar-undo")}
                    id={key}
                    message={message}
                    onUndo={onStatusChangeSnackbarUndo}
                />
            ),
            onClose: (_, reason) => {
                if (reason === "timeout") {
                    onStatusChangeSnackbarClose()
                }
            },
            autoHideDuration: UNDO_TIMEOUT,
            anchorOrigin: {
                vertical: "top",
                horizontal: "center",
            },
        })
    }

    const handleStatusClose = () => setMenuPos(null)

    const handleCloseDeliveryList = () => {
        setDeliveryListState((prev) => ({ ...prev, open: false }))
    }

    return (
        <Stack gap="2rem">
            <StatusButton
                placement={Boolean(selectedRows?.length) ? "top" : "bottom"}
                hiddenStatuses={getHiddenOrderStatuses(view)}
                onStatusChange={onStatusUpdate}
                anchorEl={menuPos ?? undefined}
                handleClose={handleStatusClose}
                currentStatus={activeRow?.deliveryDetails.statusDetails.status}
            />

            {showSuccessModal?.open && (
                <StandardSuccessDialog
                    dialogType={showSuccessModal?.type}
                    open={showSuccessModal.open || true}
                    ordersCount={showSuccessModal?.ordersCount}
                    onClose={() => {
                        showSuccessModal?.closeCallback?.()
                        setShowSuccessModal({ open: false })
                    }}
                />
            )}

            {creditNoteDialog}

            {receiptModal.open && (
                <UserMeetingsReceipt
                    open={receiptModal.open}
                    onClose={handleCloseReceipt}
                    singleMode={receiptModal.singleMode}
                    meetings={receiptModal.orders}
                    id={receiptModal.activeOrderId}
                />
            )}
            <Box className={classes.table}>
                <ApiQueryTable
                    filterFns={{
                        myCustomFilterFn: globalFilterFn,
                    }}
                    globalFilterFn="myCustomFilterFn"
                    layoutMode="grid" // or "semantic"
                    enableColumnResizing={false}
                    tableInstanceRef={tableInstanceRef}
                    enableRowSelection
                    columns={columns}
                    onRowSelectionChange={setRowSelection}
                    initialState={{ density: "spacious" }}
                    muiTableBodyRowProps={(row) => {
                        const isBooked = row.row.original?.accountingDetails?.isBooked

                        return {
                            className: cx(classes.tableBodyRow, {
                                [classes.tableBodyRowAccent]: isBooked,
                            }),
                        }
                    }}
                    muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <ProductionOrderMenu
                            row={row.original}
                            openCreditNoteDialog={openCreditNoteDialog}
                            setShowReceiptModal={(id: number) => {
                                setReceiptModal({
                                    open: true,
                                    activeOrderId: id,
                                    orders:
                                        tableInstanceRef.current
                                            ?.getPrePaginationRowModel()
                                            .rows.map((row) => row.original) ?? [],
                                    singleMode: true,
                                })
                                closeMenu()
                            }}
                            handleMenuClose={closeMenu}
                            setConfirmDialogState={setConfirmDialogState}
                            menuItemClick={() => {
                                setSingleTableItemId(row.original.id)
                                closeMenu()
                            }}
                            setShowPackSlipModal={setShowPackSlipModal}
                        />,
                    ]}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            {!excludeDateTime && (
                                <TimeOrDatePicker<TimePeriodMeetingService>
                                    excludeDateTime={excludeDateTime}
                                    isDatePicker={isDatePicker}
                                    dateState={{
                                        startDate: filtersList.startDeliveryDate!, // as we set date by default it's always defined
                                        endDate: filtersList.endDeliveryDate!,
                                    }}
                                    setDateState={(newValue) => {
                                        setFiltersList((prev) => ({
                                            ...prev,
                                            startDeliveryDate: newValue.startDate,
                                            endDeliveryDate: newValue.endDate,
                                        }))
                                    }}
                                    timePeriodOptions={[...Object.values(TimePeriodMeetingService)]}
                                    timePeriod={timePeriod}
                                    handleChangeTimePeriod={(newPeriod) => {
                                        setTimePeriod(newPeriod)
                                        LocalCache().set(StorageKey.MEETING_SERVICE_TIME_PERIOD, newPeriod)
                                    }}
                                    handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                                />
                            )}

                            <FormControl fullWidth className={cx(classes.dateFilter, classes.deliveryTypeFilter)}>
                                <InputLabel id="time-period-select-label">
                                    <LocalizedStrict id="meeting-service-table-delivery-type">
                                        <>Delivery type</>
                                    </LocalizedStrict>
                                </InputLabel>
                                <Select
                                    labelId="time-period-select-label"
                                    id="time-period-select"
                                    value={deliveryType}
                                    label="Delivery type"
                                    onChange={handleChangeDeliveryType}
                                >
                                    {Object.entries(DeliveryTypeFilter).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>
                                            <LocalizedEnum base="delivery-type-filter" enumValue={value}>
                                                <>{value}</>
                                            </LocalizedEnum>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </StandardTableFilter>
                    )}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter stackClassName={classes.filterButtons} variant="right" table={table}>
                            <LocalizedStrict id="meeting-service-delivery-list">
                                <StandardButton
                                    fullWidth={false}
                                    size="small"
                                    className={classes.rightActionButton}
                                    variant="outlined"
                                    onClick={() => {
                                        setDeliveryListState({ open: true, mode: "all" })
                                    }}
                                    startIcon={<FontAwesomeIcon icon={faPrint} />}
                                >
                                    Delivery list
                                </StandardButton>
                            </LocalizedStrict>
                            <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                            <Tooltip title={l10n.getString("meeting-service-refresh")}>
                                <LoadingIconButton
                                    onClick={() => meetingServiceResult.refetch()}
                                    size="small"
                                    aria-label="refresh"
                                    className={cx(classes.refreshButton, classes.rightActionButton)}
                                    loading={meetingServiceResult.isRefetching}
                                >
                                    <FontAwesomeIcon icon={faArrowRotateRight} />
                                </LoadingIconButton>
                            </Tooltip>
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => {
                        return (
                            <TableSummaryWrap>
                                <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                    <TableNumberOfItemsBox
                                        data={table.getFilteredRowModel().rows}
                                        isLoading={meetingServiceResult.isLoading}
                                        title="Number of orders"
                                    />
                                </LocalizedStrict>

                                <TableTotalAmountBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={meetingServiceResult.isLoading}
                                    accessor={(row) => row.original.amount}
                                />
                            </TableSummaryWrap>
                        )
                    }}
                    state={{
                        rowSelection,
                        sorting,
                        globalFilter,
                        showGlobalFilter,
                    }}
                    onSortingChange={setSorting}
                    onGlobalFilterChange={setGlobalFilter}
                    onShowGlobalFilterChange={setShowGlobalFilter}
                />
            </Box>
            {showPackSlipModal && (
                <OrderPackageSlip
                    orders={getFilteredTableData(tableInstanceRef)}
                    onClose={handleClosePackSlip}
                    merchantId={merchantId}
                    id={singleTableItemId}
                    orderIds={orderIds}
                    open={showPackSlipModal}
                />
            )}
            {showStatusChangeModal && (
                <LocalizedStrict id="meeting-service-batch-modal-title" attrs={{ dialogTitle: true }}>
                    <StandardDialog
                        open={showStatusChangeModal}
                        onClose={handleCloseBatchModal}
                        dialogTitle="Change status"
                        maxWidthProp="lg"
                    >
                        <StatusChangeModal
                            setShowUndo={setShowUndo}
                            onClose={handleCloseBatchModal}
                            selectedStatus={selectedStatus!}
                            allowedOrderIds={allowedOrderIds}
                            orders={activeRow ? [activeRow] : selectedRows!}
                            meetingServiceDependencies={meetingServiceDependencies}
                        />
                    </StandardDialog>
                </LocalizedStrict>
            )}
            {deliveryListState.open && (
                <OrderDeliveryList
                    open={deliveryListState.open}
                    onClose={handleCloseDeliveryList}
                    orderIds={orderIds}
                    mode={deliveryListState.mode}
                    meetingServiceDependencies={meetingServiceDependencies}
                    tab={tab}
                />
            )}
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict id="meeting-service-table-floating-action-bar-status" attrs={{ title: true }}>
                        <ActionBarItem icon={faArrowProgress} title="Status" onClick={onStatusClick("top")} />
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-lock-unlock" attrs={{ title: true }}>
                        <ActionBarMenu icon={faLock} title={l10n.getString("meeting-service-table-lock-column")}>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-lock-tooltip"
                                attrs={{ title: true }}
                            >
                                <Tooltip
                                    arrow
                                    title={l10n.getString("meeting-service-table-floating-action-bar-lock-tooltip")}
                                >
                                    <Box>
                                        <LocalizedStrict
                                            id="meeting-service-table-floating-action-bar-lock"
                                            attrs={{ title: true }}
                                        >
                                            <ActionBarMenuItem
                                                icon={faLock}
                                                title="Lock"
                                                onClick={() => handleLockStatusChange(orderIds, true)}
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                </Tooltip>
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-unlock-tooltip"
                                attrs={{ title: true }}
                            >
                                <Tooltip
                                    arrow
                                    title={l10n.getString("meeting-service-table-floating-action-bar-unlock-tooltip")}
                                >
                                    <Box>
                                        <LocalizedStrict
                                            id="meeting-service-table-floating-action-bar-unlock"
                                            attrs={{ title: true }}
                                        >
                                            <ActionBarMenuItem
                                                icon={faLockOpen}
                                                title="Unlock"
                                                onClick={() => handleLockStatusChange(orderIds, false)}
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                </Tooltip>
                            </LocalizedStrict>
                        </ActionBarMenu>
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-delete" attrs={{ title: true }}>
                        <ActionBarItem
                            icon={faTrash}
                            title="Delete"
                            onClick={() =>
                                setConfirmDialogState({
                                    showConfirmDialog: true,
                                    items: selectedRows,
                                    type: DialogType.DELETE,
                                })
                            }
                        />
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-print" attrs={{ title: true }}>
                        <ActionBarMenu icon={faPrint} title="Print">
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-delivery-list"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faList}
                                    title="Delivery list"
                                    onClick={() => {
                                        setDeliveryListState({ open: true, mode: "selected" })
                                    }}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-production-slip"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faList}
                                    title="Packing slip"
                                    onClick={() => {
                                        setShowPackSlipModal(true)
                                    }}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-receipt"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faReceipt}
                                    title="Receipt"
                                    onClick={() => {
                                        setReceiptModal({ open: true, orders: selectedRows, singleMode: false })
                                    }}
                                />
                            </LocalizedStrict>
                        </ActionBarMenu>
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-export" attrs={{ title: true }}>
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export selected"
                            onClick={() => {
                                exportSelectedOrders()
                            }}
                            containerStyle={classes.exportSelectedContainer}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}
            {confirmDialogState.showConfirmDialog &&
                (confirmDialogState.type === DialogType.DELETE ? (
                    <ConfirmModal
                        hideCloseIcon={false}
                        open={confirmDialogState.showConfirmDialog}
                        title={l10n.getString("meeting-service-delete-orders-title")}
                        buttonLabelSubmit={l10n.getString("meeting-service-delete-orders-button")}
                        buttonSubmitLoading={isDeletingOrders}
                        handleClick={() => {
                            const ids = confirmDialogState.items.map((order) => order.id)
                            callDeleteOrders({ orderIds: ids, cancelResourceBooking, message: cancellationMessage })
                        }}
                        onClose={() => {
                            setConfirmDialogState(defaultDialogState)
                            setCancellationMessage("")
                        }}
                        maxWidthProp="lg"
                        classNameOverride={classes.classNameOverride}
                    >
                        <MeetingServiceDeleteDialogContent
                            hasDeleteMessage
                            list={confirmDialogState.items}
                            checked={cancelResourceBooking}
                            cancellationMessage={cancellationMessage}
                            hideBooking
                            setCancellationMessage={setCancellationMessage}
                            handleChange={() => setCancelResourceBooking((prev) => !prev)}
                        />
                    </ConfirmModal>
                ) : (
                    <ConfirmModal
                        open={confirmDialogState.showConfirmDialog}
                        title={
                            <LocalizedStrict id="meeting-service-book-orders-title">
                                <span>Book orders</span>
                            </LocalizedStrict>
                        }
                        buttonLabelSubmit={l10n.getString("meeting-service-book-orders-button")}
                        handleClick={() => {
                            const list = confirmDialogState.items.map((order) => {
                                return {
                                    id: order.id,
                                    ...(order.id ? { cashPaymentDate: "2024-02-22" } : {}),
                                }
                            })
                            callChangeBookStatus({ orderIds: list })
                        }}
                        onClose={() => setConfirmDialogState(defaultDialogState)}
                        maxWidthProp="lg"
                        classNameOverride={classes.classNameOverride}
                    >
                        <MeetingServiceBookDialogContent list={confirmDialogState.items} />
                    </ConfirmModal>
                ))}

            <FiltersDrawerMeetingService
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
                disabledFilters={disabledFilters}
            />
        </Stack>
    )
}
